import {DatePickerInput, Dropdown, TextInput, TimePicker, Toggle} from '@/components/atoms'
import {SectionTitle} from '@/components/molecules'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {combineLocalDateAndTimeToUtc, splitUtcToLocalDate} from '@/helpers/dateUtils.js'
import {selectCountryCode, selectIsMexico} from '@/store/slices/countryCodeSlice'
import {PromotionType, RewardType} from '@/types/promotion'
import {getTimezoneByCountryCode} from '@/utils/constants/timeZones'
import {useEffect, useMemo} from 'react'
import {useSelector} from 'react-redux'
import './_promotionsAttributes.scss'

const PromotionsAttributes = () => {
  const {promotionType, promotion, onResultChange, promotionConfig} = usePromotion()
  const countryCode = useSelector(selectCountryCode)
  const isMexico = useSelector(selectIsMexico)
  const timezone = getTimezoneByCountryCode(countryCode)
  const activeStartDateDisplay = splitUtcToLocalDate(promotion.activeStartDate)
  const activeEndDateDisplay = splitUtcToLocalDate(promotion.activeEndDate)

  const frequencyOptions = useMemo(
    () =>
      promotionConfig?.resetFrequency?.map(frequency => ({
        displayText: frequency,
        id: frequency.split(' ')[0],
        isBadge: false,
      })) || [],
    [promotionConfig?.resetFrequency]
  )

  const behaviorOptions = useMemo(
    () => promotionConfig?.behavior?.map(behavior => ({displayText: behavior, id: behavior, isBadge: false})) || [],
    [promotionConfig?.behavior]
  )

  // Initialize default values when component mounts
  useEffect(() => {
    const needsDefaults = promotion.resetFrequencyInDays === 0 || !promotion.behavior

    if (!needsDefaults) return

    const updates = {}

    if (promotion.resetFrequencyInDays === 0) {
      updates.resetFrequencyInDays = parseInt(frequencyOptions[0]?.id)
    }

    if (!promotion.behavior) {
      updates.behavior = behaviorOptions[0]?.displayText
    }

    if (Object.keys(updates).length > 0) {
      onResultChange(updates)
    }
  }, [behaviorOptions, frequencyOptions, promotion.resetFrequencyInDays, promotion.behavior, onResultChange])

  const handleInputChange = (value, field) => {
    onResultChange({[field]: value})
  }

  const handleDateChange = (formattedDate, field) => {
    if (!formattedDate) return

    if (field === 'activeStartDate') {
      const combined = combineLocalDateAndTimeToUtc({
        date: formattedDate,
        time: promotion.activeStartTime || '00:00:00',
        timezone: timezone,
      })
      onResultChange({activeStartDate: combined})
    } else if (field === 'activeEndDate') {
      const combined = combineLocalDateAndTimeToUtc({
        date: formattedDate,
        time: promotion.activeEndTime || '00:00:00',
        timezone: timezone,
      })
      onResultChange({activeEndDate: combined})
    } else {
      onResultChange({[field]: formattedDate})
    }
  }

  const handleTimeChange = (formattedTime, timeField, dateField) => {
    const rawDate = dateField === 'activeStartDate' ? activeStartDateDisplay : activeEndDateDisplay
    const updates = {[timeField]: formattedTime}

    if (rawDate) {
      const combined = combineLocalDateAndTimeToUtc({
        date: rawDate,
        time: formattedTime || '00:00:00',
        timezone: timezone,
      })
      if (combined) {
        updates[dateField] = combined
      }
    }

    onResultChange(updates)
  }

  const handleDropdownChange = (value, field) => {
    if (field === 'resetFrequencyInDays') {
      // Convert to integer for reset frequency
      onResultChange({[field]: parseInt(value[0].id)})
    } else if (field === 'behavior') {
      // Store the display text for behavior
      onResultChange({[field]: value[0].displayText})
    }
  }

  const renderAdditionalAttributes = () => {
    // here we determine the selected reward type for Buy product or buy multiple and render field depending upon the
    // selected reward type

    const rewardType = promotion.promotionConditions[0]?.rewards?.[0].type

    switch (rewardType) {
      case RewardType.FREE_PRODUCT:
      case RewardType.GIFT_ITEM:
        return (
          <div className="">
            <TextInput
              feedbackType="info"
              inputSize="medium"
              label="Max Free Items Per Order"
              onChange={value => handleInputChange(parseInt(value) || 0, 'maxFreeItemsPerOrder')}
              onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxFreeItemsPerOrder')}
              placeholderText="10"
              value={promotion.maxFreeItemsPerOrder}
            />
          </div>
        )
      case RewardType.POINTS:
        return (
          <div className="font-semibold">
            <TextInput
              feedbackType="info"
              inputSize="medium"
              label="Max Times Per Order"
              onChange={value => handleInputChange(parseInt(value) || 0, 'maxTimesPerOrder')}
              onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxTimesPerOrder')}
              placeholderText="1"
              value={promotion.maxTimesPerOrder}
            />
          </div>
        )
      default:
        return (
          <div className="font-semibold">
            <TextInput
              feedbackType="info"
              inputSize="medium"
              label="Max Allowed Percentage"
              onChange={value => handleInputChange(parseInt(value) || 0, 'maxAllowedPercentage')}
              onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxAllowedPercentage')}
              placeholderText="100"
              value={promotion.maxAllowedPercentage}
            />
          </div>
        )
    }
  }

  // Helper function to get the selected frequency option
  const getSelectedFrequencyOption = () => {
    const option = frequencyOptions.find(opt => opt.id === promotion.resetFrequencyInDays?.toString())
    return option ? [option] : [frequencyOptions[0]]
  }

  // Helper function to get the selected behavior option
  const getSelectedBehaviorOption = () => {
    const option = behaviorOptions.find(opt => opt.displayText.toLowerCase() === promotion.behavior?.toLowerCase())
    return option ? [option] : [behaviorOptions[0]]
  }

  return (
    <div className="mt-4 rounded-lg bg-[#1D1D1D] p-4">
      <SectionTitle label={promotionType} />
      <div className="border-t border-t-[#3A3A3A]"></div>

      <SectionTitle label="Availability" />
      <div className="my-6 grid grid-cols-4 items-center gap-6">
        <div className="">
          <DatePickerInput
            value={activeStartDateDisplay}
            updateValue={formattedDate => handleDateChange(formattedDate, 'activeStartDate')}
            uiSchema={{
              placeholder: 'Start Date',
              label: 'Active Start Date*',
              className: '',
              feedbackType: 'info',
              required: true,
              format: 'yyyy-mm-dd',
              dependentDate: activeEndDateDisplay,
              validationRules: [
                {type: 'startDate', msg: "can't be in the past"},
                {type: 'startDate', endDate: activeEndDateDisplay},
              ],
            }}
            isMexico={isMexico}
          />
        </div>
        <div className="">
          <DatePickerInput
            value={activeEndDateDisplay}
            updateValue={formattedDate => handleDateChange(formattedDate, 'activeEndDate')}
            uiSchema={{
              placeholder: 'End Date',
              label: 'Active End Date*',
              className: '',
              feedbackType: 'info',
              required: true,
              format: 'yyyy-mm-dd',
              dependentDate: activeStartDateDisplay,
              validationRules: [{type: 'endDate', startDate: activeStartDateDisplay}],
            }}
          />
        </div>
        <Toggle
          isDark
          uiSchema={{size: 'small', label: 'Active'}}
          value={promotion.active ?? true}
          updateValue={checked => handleInputChange(checked, 'active')}
        />
      </div>

      <div className="my-6 grid grid-cols-4 gap-6">
        <div className="">
          <TimePicker
            value={promotion.activeStartTime || ''}
            updateValue={time => {
              handleTimeChange(time, 'activeStartTime', 'activeStartDate')
            }}
            uiSchema={{
              label: 'Active Start Time*',
              className: '',
            }}
          />
        </div>
        <div className="">
          <TimePicker
            value={promotion.activeEndTime || ''}
            updateValue={time => {
              handleTimeChange(time, 'activeEndTime', 'activeEndDate')
            }}
            uiSchema={{
              label: 'Active End Time*',
              className: '',
              dependentDates: [activeStartDateDisplay, activeEndDateDisplay],
              validationRules: [
                {
                  type: 'endTime',
                  startDate: activeStartDateDisplay,
                  startTime: promotion.activeStartTime,
                  endDate: activeEndDateDisplay,
                },
              ],
            }}
          />
        </div>
      </div>

      <div className="border-t border-t-[#3A3A3A]"></div>
      <SectionTitle label="Additional Attributes" />
      <div className="my-6 grid grid-cols-4 gap-6">
        {renderAdditionalAttributes()}
        <div className="font-semibold">
          <TextInput
            feedbackType="info"
            inputSize="medium"
            label="Max Allowed Order Per Store"
            onChange={value => handleInputChange(parseInt(value) || 0, 'maxAllowedOrderPerStore')}
            onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxAllowedOrderPerStore')}
            placeholderText="5"
            value={promotion.maxAllowedOrderPerStore}
          />
        </div>
        {promotionType !== PromotionType.WELCOME_DISCOUNT && (
          <>
            <div className="">
              <label className="dropdown-label text-sm font-normal">Reset Frequency</label>
              <Dropdown
                isJsonWrap={false}
                childList={frequencyOptions}
                isDark={true}
                selection="single"
                isClearable={false}
                updateValue={value => handleDropdownChange(value, 'resetFrequencyInDays')}
                className="dropdown"
                size="medium"
                value={getSelectedFrequencyOption()}
                id="resetFrequency"
              />
            </div>
            <div className="">
              <label className="dropdown-label text-sm font-normal">Behavior</label>
              <Dropdown
                isJsonWrap={false}
                childList={behaviorOptions}
                isDark={true}
                selection="single"
                isClearable={false}
                updateValue={value => handleDropdownChange(value, 'behavior')}
                className="dropdown"
                size="medium"
                value={getSelectedBehaviorOption()}
              />
            </div>
          </>
        )}
        <div></div>
      </div>
    </div>
  )
}

export default PromotionsAttributes
